import React from 'react'
import { Clock, MapPin, Users, ExternalLink } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'

interface EventCardProps {
  event: CalendarEvent
  onClick?: () => void
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick }) => {
  const formatTime = (dateString: string) => {
    try {
      const date = parseISO(dateString)
      return format(date, 'HH:mm', { locale: ptBR })
    } catch {
      return ''
    }
  }

  const startTime = formatTime(event.start)
  const endTime = formatTime(event.end)

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-neutral-800/50 hover:bg-neutral-800 border border-neutral-700 rounded-lg p-4 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-neutral-50 truncate">
            {event.title || 'Sem titulo'}
          </h3>

          <div className="mt-2 space-y-1.5">
            {(startTime || endTime) && (
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>
                  {startTime}
                  {endTime && ` - ${endTime}`}
                </span>
              </div>
            )}

            {event.location && (
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{event.location}</span>
              </div>
            )}

            {event.attendees && event.attendees.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Users className="w-4 h-4 flex-shrink-0" />
                <span>{event.attendees.length} participante(s)</span>
              </div>
            )}
          </div>
        </div>

        {event.html_link && (
          <a
            href={event.html_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="p-2 hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-neutral-400" />
          </a>
        )}
      </div>
    </button>
  )
}

export default EventCard
